import React, { useRef, useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { useGlobalFontSize } from '../../context/GlobalFontSizeContext';
import FloatingLabel from '../../components/FloatingLabel';
import {
  FILTER_CHEVRON_CLASS,
  FILTER_CHEVRON_SIZE,
  FILTER_LABEL_CLASS,
  FILTER_PLACEHOLDER_TEXT_CLASS,
  FILTER_VALUE_CLASS,
  FILTER_VALUE_TEXT_CLASS,
  filterTriggerClass,
} from './filterControlStyles';

/**
 * @param {'floating'|'stacked'} variant  `floating` keeps the label inside the
 *   control (legacy Terminal/Map strips). `stacked` renders the label above a
 *   compact, unfilled control — the Analytics top filter bar design.
 */
const DateDropdown = ({
  value,
  onChange,
  label = '',
  variant = 'floating',
  placeholder = 'Select...',
}) => {
  const { fontSize } = useGlobalFontSize();
  const inputRef = useRef(null);
  const [isFocused, setIsFocused] = useState(false);

  const formatDisplayDate = (dateString) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString + 'T00:00:00');
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      });
    } catch {
      return '';
    }
  };

  const handleInputClick = (e) => {
    e.stopPropagation();
    setTimeout(() => {
      inputRef.current?.showPicker?.();
    }, 0);
  };

  const handleFocus = () => setIsFocused(true);
  const handleBlur = () => setIsFocused(false);

  const nativeInput = (
    <input
      ref={inputRef}
      type="date"
      value={value || ''}
      onChange={(e) => onChange(e.target.value)}
      onClick={handleInputClick}
      onFocus={handleFocus}
      onBlur={handleBlur}
      className="absolute inset-0 opacity-0 cursor-pointer w-full h-full z-10"
    />
  );

  if (variant === 'stacked') {
    return (
      <div className="min-w-0">
        {label ? <label className={FILTER_LABEL_CLASS}>{label}</label> : null}
        <div className={`relative w-full min-w-0 ${filterTriggerClass()}`}>
          <span
            className={`${FILTER_VALUE_CLASS} ${
              value ? FILTER_VALUE_TEXT_CLASS : FILTER_PLACEHOLDER_TEXT_CLASS
            }`}
          >
            {formatDisplayDate(value) || placeholder}
          </span>
          <ChevronDown size={FILTER_CHEVRON_SIZE} className={FILTER_CHEVRON_CLASS} />
          {nativeInput}
        </div>
      </div>
    );
  }

  return (
    <div
      className="relative flex items-center bg-white dark:bg-[#282a29] border border-bloom-green rounded-lg h-8 px-2 sm:px-3 w-full sm:w-auto sm:min-w-[136px] min-w-0 cursor-pointer hover:bg-gray-50 dark:hover:bg-[#333534] transition-colors duration-200"
    >
      <FloatingLabel
        label={label}
        isFocused={isFocused}
        hasValue={!!value}
      />

      <span
        className={`font-medium truncate pointer-events-none ${
          value ? 'text-[#444746] dark:text-[#f0f5f2]' : 'text-transparent'
        }`}
        style={{ fontSize: fontSize - 2 }}
      >
        {formatDisplayDate(value) || ' '}
      </span>

      {nativeInput}
    </div>
  );
};

export default DateDropdown;
